import { useEffect, useMemo, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Pencil,
  Trash2,
  Plus,
  Users,
  Shield,
  Rocket,
  ArrowDownCircle,
  Download
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import { fetchJson, type AdminOrg, type SessionUser } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { useConfirm } from '@/components/ConfirmDialog';

async function downloadZip(url: string, fallbackName: string): Promise<void> {
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) throw new Error('download_failed');
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  const cd = res.headers.get('content-disposition') || '';
  const m = cd.match(/filename="([^"]+)"/);
  a.download = m ? m[1] : fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}

function suggestSlug(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^\x00-\x7F]/g, '')
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 41);
}

function CreateOrgDialog({
  open,
  onOpenChange,
  onCreated
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!slugTouched) setSlug(suggestSlug(name));
  }, [name, slugTouched]);

  const slugValid = /^[a-z][a-z0-9-]{2,40}$/.test(slug);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>יצירת ארגון חדש</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!slugValid) return;
            setBusy(true);
            try {
              await fetchJson('/api/admin/orgs', {
                method: 'POST',
                body: JSON.stringify({ name: name.trim(), slug: slug.trim() })
              });
              toast.success('הארגון נוצר');
              onCreated();
              onOpenChange(false);
              setName('');
              setSlug('');
              setSlugTouched(false);
            } catch (err: unknown) {
              const e = err as { message?: string };
              toast.error(e.message === 'slug_taken' ? 'ה-slug כבר תפוס' : e.message || 'שגיאה');
            } finally {
              setBusy(false);
            }
          }}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="org_name">שם הארגון *</Label>
            <Input id="org_name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="org_slug">Slug (אנגלית, ייחודי) *</Label>
            <Input
              id="org_slug"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugTouched(true);
              }}
              required
              dir="ltr"
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              {slugValid ? '✓ תקין' : 'אותיות קטנות + ספרות + מקפים, מתחיל באות, 3-41 תווים'}
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              ביטול
            </Button>
            <Button type="submit" disabled={!slugValid || busy}>
              {busy ? 'יוצר...' : 'צור'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditOrgDialog({
  org,
  onOpenChange,
  onSaved
}: {
  org: AdminOrg;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(org.name);
  const [busy, setBusy] = useState(false);

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>עריכת ארגון</DialogTitle>
          <DialogDescription>
            slug: <code className="font-mono">{org.slug}</code> (לא ניתן לשינוי)
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            try {
              await fetchJson(`/api/admin/orgs/${org.id}`, {
                method: 'PATCH',
                body: JSON.stringify({ name: name.trim() })
              });
              toast.success('עודכן');
              onSaved();
              onOpenChange(false);
            } catch (err: unknown) {
              const e = err as { message?: string };
              toast.error(e.message || 'שגיאה');
            } finally {
              setBusy(false);
            }
          }}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="edit_name">שם הארגון</Label>
            <Input id="edit_name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              ביטול
            </Button>
            <Button type="submit" disabled={busy}>
              שמור
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ManageAdminsDialog({
  org,
  onOpenChange,
  user
}: {
  org: AdminOrg;
  onOpenChange: (o: boolean) => void;
  user: SessionUser;
}) {
  const confirm = useConfirm();
  const [admins, setAdmins] = useState<{ email: string; addedAt: string; addedBy: string | null }[]>(
    []
  );
  const [newEmail, setNewEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const list = await fetchJson<{ email: string; addedAt: string; addedBy: string | null }[]>(
      `/api/admin/orgs/${org.id}/admins`
    );
    setAdmins(list);
  }, [org.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>ניהול מנהלי {org.name}</DialogTitle>
          <DialogDescription>מי יכול להיכנס לדשבורד של הארגון</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="email@example.com"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              dir="ltr"
            />
            <Button
              disabled={!newEmail.includes('@') || busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await fetchJson(`/api/admin/orgs/${org.id}/admins`, {
                    method: 'POST',
                    body: JSON.stringify({ email: newEmail })
                  });
                  toast.success('נוסף');
                  setNewEmail('');
                  refresh();
                } catch (err: unknown) {
                  const e = err as { message?: string };
                  toast.error(e.message || 'שגיאה');
                } finally {
                  setBusy(false);
                }
              }}
            >
              הוסף
            </Button>
          </div>
          <div className="max-h-72 overflow-auto">
            {admins.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">אין מנהלים</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>מייל</TableHead>
                    <TableHead>נוסף בתאריך</TableHead>
                    <TableHead className="text-end">פעולות</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {admins.map((a) => (
                    <TableRow key={a.email}>
                      <TableCell className="font-mono text-xs" dir="ltr">
                        {a.email}
                      </TableCell>
                      <TableCell className="text-sm">{formatDate(a.addedAt)}</TableCell>
                      <TableCell className="text-end">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={async () => {
                            const ok = await confirm({
                              title: `להסיר את ${a.email}?`,
                              description: 'מנהל זה לא יוכל יותר לגשת לארגון.',
                              confirmText: 'הסר',
                              variant: 'destructive'
                            });
                            if (!ok) return;
                            await fetchJson(
                              `/api/admin/orgs/${org.id}/admins/${encodeURIComponent(a.email)}`,
                              { method: 'DELETE' }
                            );
                            refresh();
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            המייל שלך ({user.email}) מוגדר כ-super admin ב-ENV ויש לך גישה לכל הארגונים.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DeleteOrgDialog({
  org,
  onOpenChange,
  onDeleted
}: {
  org: AdminOrg;
  onOpenChange: (o: boolean) => void;
  onDeleted: () => void;
}) {
  const [confirmation, setConfirmation] = useState('');
  const [downloadBackup, setDownloadBackup] = useState(true);
  const valid = confirmation === org.slug;

  async function handleDelete() {
    if (!valid) return;
    if (downloadBackup) {
      // Server returns ZIP stream and deletes after stream completes.
      const url = `/api/admin/orgs/${org.id}`;
      const csrf =
        document.cookie.match(/(?:^|; )csrf=([^;]*)/)?.[1] || '';
      const res = await fetch(url, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': decodeURIComponent(csrf) },
        credentials: 'same-origin'
      });
      if (!res.ok) {
        toast.error('שגיאה בהורדת הגיבוי');
        return;
      }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const cd = res.headers.get('content-disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : `${org.slug}-backup.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('הגיבוי הורד והארגון נמחק');
    } else {
      await fetchJson(`/api/admin/orgs/${org.id}?download=false`, { method: 'DELETE' });
      toast.success('הארגון נמחק');
    }
    onDeleted();
    onOpenChange(false);
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-destructive">מחיקת הארגון "{org.name}"</DialogTitle>
          <DialogDescription>
            פעולה זו תמחק את הארגון ואת כל הלקוחות, השאלונים, המחזורים והרשומות שלו. לא ניתן לבטל.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="confirm">
              כדי לאשר, הקלד את ה-slug: <code className="font-mono">{org.slug}</code>
            </Label>
            <Input
              id="confirm"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              dir="ltr"
              className="font-mono"
            />
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={downloadBackup}
              onCheckedChange={setDownloadBackup}
              id="download_backup"
            />
            <Label htmlFor="download_backup">הורד גיבוי ZIP לפני מחיקה</Label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              ביטול
            </Button>
            <Button variant="destructive" disabled={!valid} onClick={handleDelete}>
              מחק
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminOrgs({ user }: { user: SessionUser }) {
  const confirm = useConfirm();
  const [orgs, setOrgs] = useState<AdminOrg[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AdminOrg | null>(null);
  const [managing, setManaging] = useState<AdminOrg | null>(null);
  const [deleting, setDeleting] = useState<AdminOrg | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchJson<AdminOrg[]>('/api/admin/orgs');
      setOrgs(list);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const columns: DataTableColumn<AdminOrg>[] = useMemo(
    () => [
      { key: 'name', header: 'שם', cell: (o) => <span className="font-medium">{o.name}</span>, exportValue: (o) => o.name },
      {
        key: 'slug',
        header: 'Slug',
        cell: (o) => <span className="font-mono text-xs">{o.slug}</span>,
        exportValue: (o) => o.slug
      },
      { key: 'adminCount', header: 'מנהלים', cell: (o) => o.adminCount, exportValue: (o) => o.adminCount },
      { key: 'customerCount', header: 'לקוחות', cell: (o) => o.customerCount, exportValue: (o) => o.customerCount },
      {
        key: 'recordsThisMonth',
        header: 'רשומות החודש',
        cell: (o) => o.recordsThisMonth,
        exportValue: (o) => o.recordsThisMonth
      },
      {
        key: 'plan',
        header: 'תוכנית',
        cell: (o) =>
          o.plan === 'active' ? (
            <Badge variant="success">פעיל</Badge>
          ) : (
            <Badge variant={o.trialExpired ? 'destructive' : 'muted'}>
              {o.trialExpired ? 'ניסיון פג' : 'ניסיון'}
            </Badge>
          ),
        exportValue: (o) =>
          o.plan === 'active' ? 'פעיל' : o.trialExpired ? 'ניסיון פג' : 'ניסיון'
      },
      {
        key: 'trialEndsAt',
        header: 'תום ניסיון',
        cell: (o) => (o.trialEndsAt ? <span className="text-xs">{formatDate(o.trialEndsAt)}</span> : '-'),
        exportValue: (o) => (o.trialEndsAt ? formatDate(o.trialEndsAt) : '')
      },
      {
        key: 'createdAt',
        header: 'נוצר בתאריך',
        cell: (o) => <span className="text-sm">{formatDate(o.createdAt)}</span>,
        exportValue: (o) => formatDate(o.createdAt)
      },
      {
        key: 'createdBy',
        header: 'נוצר ע"י',
        cell: (o) => (
          <span className="text-xs text-muted-foreground" dir="ltr">
            {o.createdBy || '-'}
          </span>
        ),
        exportValue: (o) => o.createdBy || ''
      },
      {
        key: 'actions',
        header: 'פעולות',
        align: 'end',
        exportable: false,
        stopClickPropagation: true,
        cell: (o) => (
          <div className="flex justify-end gap-1">
            {o.plan === 'trial' ? (
              <Button
                variant="ghost"
                size="icon"
                title="שדרג לחשבון מלא"
                onClick={async () => {
                  const ok = await confirm({
                    title: `לשדרג את "${o.name}" לחשבון מלא?`,
                    description: 'הארגון יסומן כפעיל ללא הגבלת זמן.',
                    confirmText: 'שדרג'
                  });
                  if (!ok) return;
                  await fetchJson(`/api/admin/orgs/${o.id}/plan`, {
                    method: 'PATCH',
                    body: JSON.stringify({ plan: 'active' })
                  });
                  toast.success('הארגון שודרג לחשבון מלא');
                  refresh();
                }}
              >
                <Rocket className="h-4 w-4 text-primary" />
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                title="הסר תוכנית מלאה"
                onClick={async () => {
                  const ok = await confirm({
                    title: `להסיר את התוכנית המלאה מ-"${o.name}"?`,
                    description:
                      'הארגון יחזור למצב ניסיון שפג, וייחסם עד לשדרוג מחדש. הנתונים נשמרים.',
                    confirmText: 'הסר תוכנית',
                    variant: 'destructive'
                  });
                  if (!ok) return;
                  await fetchJson(`/api/admin/orgs/${o.id}/plan`, {
                    method: 'PATCH',
                    body: JSON.stringify({ plan: 'trial', days: 0 })
                  });
                  toast.success('התוכנית המלאה הוסרה');
                  refresh();
                }}
              >
                <ArrowDownCircle className="h-4 w-4 text-destructive" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              title="הורדת נתונים גולמיים"
              onClick={async () => {
                try {
                  await downloadZip(
                    `/api/admin/orgs/${o.id}/backup.zip`,
                    `${o.slug}-backup.zip`
                  );
                  toast.success('הגיבוי הורד');
                } catch {
                  toast.error('שגיאה בהורדת הגיבוי');
                }
              }}
            >
              <Download className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setManaging(o)}>
              <Users className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setEditing(o)}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setDeleting(o)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        )
      }
    ],
    [confirm, refresh]
  );

  return (
    <div className="page-fill flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            <h1 className="font-heading text-2xl font-semibold tracking-tight">ניהול ארגונים</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            יצירה, עריכה ומחיקה של ארגונים, וניהול מנהלים שלהם
          </p>
        </div>
        <Button size="sm" onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" />
          צור ארגון חדש
        </Button>
      </div>

      <Card className="flex min-h-0 flex-1 flex-col">
        <CardHeader className="flex-none">
          <CardTitle>{orgs.length} ארגונים</CardTitle>
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 flex-col">
          <DataTable
            columns={columns}
            data={orgs}
            getRowKey={(o) => o.id}
            loading={loading}
            emptyMessage="עדיין אין ארגונים. צור את הראשון כדי להתחיל."
            exportFileName="ארגונים"
            exportSheetName="ארגונים"
            pagination
          />
        </CardContent>
      </Card>

      <CreateOrgDialog open={creating} onOpenChange={setCreating} onCreated={refresh} />
      {editing && (
        <EditOrgDialog org={editing} onOpenChange={() => setEditing(null)} onSaved={refresh} />
      )}
      {managing && (
        <ManageAdminsDialog org={managing} onOpenChange={() => setManaging(null)} user={user} />
      )}
      {deleting && (
        <DeleteOrgDialog
          org={deleting}
          onOpenChange={() => setDeleting(null)}
          onDeleted={refresh}
        />
      )}
    </div>
  );
}
